'use client'

import { useState, useCallback } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, A11y } from 'swiper/modules'

import clsx from 'clsx'
import type {
  TypeMedicalGroup,
  TypePlacePublic,
  TypeScheduleAmended,
  TypeScheduleException,
} from '@lib/location-types'

import type { UseHitsProps } from 'react-instantsearch'
import { useConfigure, useHits } from 'react-instantsearch'
import {
  convertToDisplayReadySchedule,
  getScheduleSummaryInfo,
} from '@lib/utilities/location-hours-utilities'
import LocationHoursSummary from '@app/[locale]/(main)/locations/_components/location-hours-summary'
import LocationSaveaSpotSummary from '@app/[locale]/(main)/locations/_components/location-save-a-spot-summary'
import { WaitTimesStatusEnum } from '@app/[locale]/(main)/search/_utils/constants'
import SearchLocationWaitTimes from '@app/[locale]/(main)/search/_components/search-location-wait-times'
import { algoliaUtilites } from '@lib/utilities/algolia-utilities'
import { Button, Icon, Typography, Card } from '@shc/ui'
import useAnalytics from '@hooks/use-analytics'
import NextLink from 'next/link'

type LocationNearByUrgentCaresProps = {
  now: string
  location: TypePlacePublic
}

const LocationNearByUrgentCares = ({ now, location }: LocationNearByUrgentCaresProps) => {
  const { trackSnowplow } = useAnalytics()
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null)
  const [disable, setDisable] = useState({ prev: true, next: false })

  // configure search
  useConfigure({
    aroundRadius: 'all',
    aroundLatLng: `${location.lat},${location.lon}`,
    aroundLatLngViaIP: false,
    getRankingInfo: true,
    /* Algolia hooks */
    // when component containing this hook mounts, initial search to Algolia is triggered.
    filters: algoliaUtilites.nearbyUCSearch(location.medical_groups as TypeMedicalGroup[]),
  })

  const transformItems: UseHitsProps['transformItems'] = useCallback((items: any) => {
    return items
      .map((hit: any) => {
        const schedule = hit.schedules as TypeScheduleAmended[]
        const scheduleExceptions = hit.schedule_exceptions as TypeScheduleException[]
        const displayReadySchedule = convertToDisplayReadySchedule(
          now,
          schedule,
          scheduleExceptions
        )

        const { operationalStatus, nextOpenTime, locationExceptionText } = getScheduleSummaryInfo(
          now,
          displayReadySchedule
        )

        const waitTimeAvailable = [
          WaitTimesStatusEnum.Normal,
          WaitTimesStatusEnum.HighVolume,
          WaitTimesStatusEnum.SoftClosure,
        ].includes(hit.wait_time_status)

        const isLocationOpen = operationalStatus === 'Open' || operationalStatus === 'Open 24 hours'

        return {
          ...hit,
          operationalStatus,
          nextOpenTime,
          locationExceptionText,
          waitTimeAvailable,
          isLocationOpen,
        }
      })
      .filter((hit: any) => hit.id !== location.id)
      .slice(0, 5) // only show 4 results
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // pulling from the same store, but hits are more concise (subset of results)
  const { items } = useHits({ transformItems })

  if (items.length) {
    return (
      <div>
        <Typography variant="h2" className="pb-6">
          More urgent care locations
        </Typography>
        <div className="flex flex-row">
          {items.length !== 1 && (
            <div className="hidden md:flex flex-row items-center justify-center pr-3">
              <Button
                color="transparent"
                size="sm"
                shape="circle"
                width="auto"
                aria-label="Previous Slide"
                ref={(node) => setPrevEl(node)}
                disabled={disable.prev}
                className="swiperPrev text-primary-700">
                <Icon className="h-6" icon="chevron-left" />
              </Button>
            </div>
          )}
          <Swiper
            onSlideChangeTransitionStart={(swiper) => {
              setDisable({ prev: swiper.isBeginning, next: swiper.isEnd })
              trackSnowplow({
                event: { name: 'component_scroll', data: {} },
                contexts: [
                  {
                    name: 'component',
                    data: {
                      component_text: 'LocationNearByUrgentCares',
                    },
                  },
                  {
                    name: 'place',
                    data: {
                      place_id: location.id ?? -1,
                      place_name: location.name,
                    },
                  },
                ],
              })
            }}
            a11y={{
              enabled: true,
              firstSlideMessage: 'This is the first slide',
              lastSlideMessage: 'This is the last slide',
            }}
            keyboard={{ enabled: true }}
            modules={[Navigation, A11y]}
            navigation={{
              prevEl,
              nextEl,
            }}
            slidesPerView={1}
            spaceBetween={20}
            breakpoints={{
              320: {
                slidesPerView: 1.2,
              },
              640: {
                slidesPerView: 1.5,
              },
              768: {
                slidesPerView: 2,
              },
            }}>
            {items.map((hit) => {
              return (
                <SwiperSlide key={hit.objectID} className="flex w-full h-full p-3">
                  <Card
                    className="flex flex-col gap-5"
                    as={hit.slug ? NextLink : 'div'}
                    {...(hit.slug && { href: '/locations/' + hit.slug, color: 'inherit' })}>
                    <Typography
                      as="h3"
                      variant="h4"
                      className={clsx(hit.slug && 'text-primary-700')}>
                      {hit.name}
                    </Typography>
                    <LocationHoursSummary
                      accessible={true}
                      operationalStatus={hit.operationalStatus}
                      nextOpenTime={hit.nextOpenTime}
                      locationExceptionText={hit.locationExceptionText}
                    />

                    {hit.isLocationOpen && hit.waitTimeAvailable && (
                      <SearchLocationWaitTimes waitTime={hit.wait_time} className="text-sm" />
                    )}

                    {!!hit.first_available_spot && (
                      <LocationSaveaSpotSummary timeslot={hit.first_available_spot} />
                    )}
                  </Card>
                </SwiperSlide>
              )
            })}
          </Swiper>
          {items.length !== 1 && (
            <div className="hidden md:flex flex-row items-center justify-center pl-3">
              <Button
                color="transparent"
                size="sm"
                shape="circle"
                width="auto"
                aria-label="Next Slide"
                ref={(node) => setNextEl(node)}
                disabled={disable.next}
                className="swiperNext text-primary-700">
                <Icon className="h-6" icon="chevron-right" />
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default LocationNearByUrgentCares
